import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_FILTER_PRESETS_QUERY,
  GET_MY_SITES_QUERY,
  SAVE_FILTER_PRESET_MUTATION,
} from '../../gql';
import {
  cleanFindFilter,
  EMPTY_FIND_FILTER_LIST_KEYS,
  FindFilterListKey,
  FindFilterValue,
  StoredFindFilter,
} from '../FindToolbox/Filters/types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../i18n';
import { NO_SITE_FILTER_VALUE } from '../../consts';

export type FindPresetDialogMode = 'load' | 'save';

interface FilterPreset {
  id: string;
  name: string;
  filter: StoredFindFilter;
}

interface FindPresetDialogProps {
  mode: FindPresetDialogMode;
  open: boolean;
  filter: FindFilterValue | null;
  omittedKeys?: readonly FindFilterListKey[];
  onClose: () => void;
  onLoad?: (filter: StoredFindFilter | null) => void;
}

const dialogPaperProps = { sx: { width: 820, maxWidth: 'calc(100vw - 32px)' } };
const contentStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
  gap: 2,
  minHeight: 360,
  maxHeight: 'calc(100vh - 180px)',
};
const listStyles = { maxHeight: 310, overflowY: 'auto' };
const rowStyles = {
  width: '100%',
  justifyContent: 'flex-start',
  px: 1.5,
  py: 1.25,
  borderRadius: 1,
  textAlign: 'left',
};
const selectedRowStyles = {
  ...rowStyles,
  bgcolor: 'action.selected',
};
const previewStyles = {
  minWidth: 0,
  borderLeft: { xs: 0, md: '1px solid' },
  borderTop: { xs: '1px solid', md: 0 },
  borderColor: 'divider',
  pl: { xs: 0, md: 2 },
  pt: { xs: 2, md: 0 },
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: { xs: 280, md: 360 },
  pr: 0.5,
};
const titleStyles = { pr: 6 };
const closeStyles = { position: 'absolute', right: 12, top: 10 } as const;
const errorStyles = { color: 'error.main' };
const nameInputProps = { maxLength: 100 };
const staticLabelStyles = {
  color: 'text.secondary',
  fontSize: 12,
  lineHeight: 1.5,
};
const neutralInputStyles = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'sunken',
    fontSize: 13,
    '& fieldset': { borderColor: 'outline' },
    '&:hover fieldset': { borderColor: 'text.secondary' },
    '&.Mui-focused fieldset': {
      borderColor: 'text.primary',
      borderWidth: 1,
    },
  },
};
const cancelButtonStyles = {
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};
const primaryActionStyles = {
  bgcolor: 'text.primary',
  color: 'background.paper',
  '&:hover': { bgcolor: 'text.secondary' },
};

const previewMessages = defineMessages({
  sites: { id: 'map.preset.sites', defaultMessage: 'Sites' },
  groups: { id: 'map.preset.groups', defaultMessage: 'Groups' },
  eras: { id: 'map.preset.eras', defaultMessage: 'Eras' },
  findTypes: { id: 'map.preset.findTypes', defaultMessage: 'Find types' },
  materials: { id: 'map.preset.materials', defaultMessage: 'Materials' },
  tags: { id: 'map.preset.tags', defaultMessage: 'Tags' },
});

const filterLabels: Array<{
  key: keyof StoredFindFilter;
  message: typeof previewMessages[keyof typeof previewMessages];
}> = [
  { key: 'sites', message: previewMessages.sites },
  { key: 'groups', message: previewMessages.groups },
  { key: 'eras', message: previewMessages.eras },
  { key: 'findTypes', message: previewMessages.findTypes },
  { key: 'materials', message: previewMessages.materials },
  { key: 'tags', message: previewMessages.tags },
];

interface MutationResultPayload {
  __typename?: string;
  errors?: Array<{ error?: string }>;
}

function getMutationError(
  payload: MutationResultPayload | null,
  fallback: string
): string | null {
  if (payload?.__typename !== 'ValidationError') {
    return null;
  }

  return payload.errors?.[0]?.error || fallback;
}

interface PresetRowProps {
  preset: FilterPreset;
  selected: boolean;
  onSelect: (id: string) => void;
}

const PresetRow = React.memo(
  ({ preset, selected, onSelect }: PresetRowProps) => {
    const handleClick = () => onSelect(preset.id);

    return (
      <ButtonBase
        onClick={handleClick}
        sx={selected ? selectedRowStyles : rowStyles}
      >
        <Typography noWrap>{preset.name}</Typography>
      </ButtonBase>
    );
  }
);

function FilterPreview({ filter }: { filter: StoredFindFilter | null }) {
  const intl = useIntl();
  const getVocabularyLabel = useVocabulary();
  const { data: filterTypesData } = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const { data: sitesData } = useQuery(GET_MY_SITES_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const valueLabels = useMemo(() => {
    const sites = new Map<string, string>([
      [
        NO_SITE_FILTER_VALUE,
        intl.formatMessage({ id: 'general.none', defaultMessage: 'None' }),
      ],
      ...(sitesData?.getMySites || []).map((site) => [
        site.id,
        site.name || site.id,
      ]),
    ]);
    const groups = new Map<string, string>(
      (filterTypesData?.getAllFilterTypes?.groups || []).map((group) => [
        group.id,
        group.name,
      ])
    );

    return { sites, groups };
  }, [filterTypesData, intl, sitesData]);

  if (!filter) {
    return (
      <Typography color="text.secondary" fontSize={14}>
        <FormattedMessage
          id="map.preset.noFilters"
          defaultMessage="No filters."
        />
      </Typography>
    );
  }

  const rows = filterLabels
    .map(({ key, message }) => {
      const values = filter[key];
      return {
        key,
        label: intl.formatMessage(message),
        values: Array.isArray(values) ? values : [],
      };
    })
    .filter(({ values }) => values.length > 0);

  return (
    <Stack gap={1.5}>
      {rows.map(({ key, label, values }) => (
        <Stack gap={0.5} key={key}>
          <Typography fontSize={12} color="text.secondary">
            {label}
          </Typography>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {values.map((value) => (
              <Chip
                key={value}
                size="small"
                label={
                  key === 'sites'
                    ? valueLabels.sites.get(value) || value
                    : key === 'groups'
                    ? valueLabels.groups.get(value) || value
                    : key === 'eras'
                    ? getVocabularyLabel('era', value)
                    : key === 'findTypes'
                    ? getVocabularyLabel('findType', value)
                    : key === 'materials'
                    ? getVocabularyLabel('material', value)
                    : value
                }
              />
            ))}
          </Stack>
        </Stack>
      ))}
      {filter.hasImages != null && (
        <Stack gap={0.5}>
          <Typography fontSize={12} color="text.secondary">
            <FormattedMessage id="map.preset.images" defaultMessage="Images" />
          </Typography>
          <Chip
            size="small"
            label={
              filter.hasImages
                ? intl.formatMessage({
                    id: 'filter.withImages',
                    defaultMessage: 'With images',
                  })
                : intl.formatMessage({
                    id: 'filter.withoutImages',
                    defaultMessage: 'Without images',
                  })
            }
          />
        </Stack>
      )}
      {filter.hasAmcrId != null && (
        <Stack gap={0.5}>
          <Typography fontSize={12} color="text.secondary">
            <FormattedMessage id="map.preset.amcrId" defaultMessage="PAS ID" />
          </Typography>
          <Chip
            size="small"
            label={
              filter.hasAmcrId
                ? intl.formatMessage({
                    id: 'filter.withAmcrId',
                    defaultMessage: 'With PAS ID',
                  })
                : intl.formatMessage({
                    id: 'filter.withoutAmcrId',
                    defaultMessage: 'Without PAS ID',
                  })
            }
          />
        </Stack>
      )}
      {rows.length === 0 &&
        filter.hasImages == null &&
        filter.hasAmcrId == null && (
          <Typography color="text.secondary" fontSize={14}>
            <FormattedMessage
              id="map.preset.noFilters"
              defaultMessage="No filters."
            />
          </Typography>
        )}
    </Stack>
  );
}

export function FindPresetDialog({
  mode,
  open,
  filter,
  omittedKeys = EMPTY_FIND_FILTER_LIST_KEYS,
  onClose,
  onLoad,
}: FindPresetDialogProps) {
  const intl = useIntl();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data, loading } = useQuery(GET_MY_FILTER_PRESETS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });
  const [
    savePreset,
    { loading: saving },
  ] = useMutation(SAVE_FILTER_PRESET_MUTATION, {
    refetchQueries: [{ query: GET_MY_FILTER_PRESETS_QUERY }],
  });
  const presets: FilterPreset[] = data?.getMyFilterPresets || [];
  const selectedPreset =
    presets.find((preset) => preset.id === selectedId) || null;
  const currentFilter = cleanFindFilter(filter, omittedKeys);
  const previewFilter = selectedPreset?.filter || currentFilter;

  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setNewName('');
      setError(null);
    }
  }, [open]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
    setSelectedId(null);
    setError(null);
  };

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setNewName('');
    setError(null);
  }, []);

  const handlePrimaryAction = async () => {
    if (mode === 'load') {
      onLoad?.(cleanFindFilter(selectedPreset?.filter, omittedKeys));
      onClose();
      return;
    }

    const name = selectedPreset?.name || newName.trim();
    if (!name) {
      return;
    }

    const result = await savePreset({
      variables: {
        id: selectedPreset?.id || null,
        name,
        filter: currentFilter || {},
      },
    });
    const payload = result.data?.saveFilterPreset;
    const mutationError = getMutationError(
      payload,
      intl.formatMessage({
        id: 'map.preset.saveError',
        defaultMessage: 'Could not save the preset.',
      })
    );

    if (mutationError) {
      setError(mutationError);
      return;
    }

    onClose();
  };

  const canSubmit =
    mode === 'load' ? !!selectedPreset : !!selectedPreset || !!newName.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={dialogPaperProps}
    >
      <DialogTitle sx={titleStyles}>
        {mode === 'save' ? (
          <FormattedMessage
            id="map.preset.saveTitle"
            defaultMessage="Save filter preset"
          />
        ) : (
          <FormattedMessage
            id="map.preset.loadTitle"
            defaultMessage="Select filter preset to load"
          />
        )}
        <IconButton
          aria-label={intl.formatMessage({
            id: 'action.close',
            defaultMessage: 'Close',
          })}
          onClick={onClose}
          sx={closeStyles}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={contentStyles} dividers>
        <Stack gap={1.5} minWidth={0}>
          {mode === 'save' && (
            <Stack gap={0.5}>
              <Typography component="label" sx={staticLabelStyles}>
                <FormattedMessage
                  id="map.preset.newName"
                  defaultMessage="New preset name"
                />
              </Typography>
              <TextField
                autoFocus
                placeholder={intl.formatMessage({
                  id: 'map.preset.namePlaceholder',
                  defaultMessage: 'Name this filter preset',
                })}
                size="small"
                value={newName}
                onChange={handleNameChange}
                inputProps={nameInputProps}
                sx={neutralInputStyles}
              />
            </Stack>
          )}
          {mode === 'save' && <Divider />}
          <Typography fontSize={13} fontWeight={600}>
            <FormattedMessage
              id="map.preset.existing"
              defaultMessage="Existing presets"
            />
          </Typography>
          <Stack gap={0.5} sx={listStyles}>
            {loading && !data ? (
              <Box py={4} textAlign="center">
                <CircularProgress size={24} />
              </Box>
            ) : presets.length ? (
              presets.map((preset) => (
                <PresetRow
                  key={preset.id}
                  preset={preset}
                  selected={selectedId === preset.id}
                  onSelect={handleSelect}
                />
              ))
            ) : (
              <Typography color="text.secondary" fontSize={14}>
                <FormattedMessage
                  id="map.preset.noneSaved"
                  defaultMessage="No saved presets yet."
                />
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack gap={1.5} sx={previewStyles}>
          <Typography fontWeight={600}>
            <FormattedMessage
              id="map.preset.preview"
              defaultMessage="Preview"
            />
          </Typography>
          <FilterPreview filter={previewFilter} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {error && (
          <Typography mr="auto" fontSize={13} sx={errorStyles}>
            {error}
          </Typography>
        )}
        <Button onClick={onClose} sx={cancelButtonStyles}>
          <FormattedMessage id="action.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit || saving}
          onClick={handlePrimaryAction}
          sx={primaryActionStyles}
        >
          {mode === 'save' ? (
            <FormattedMessage id="action.save" defaultMessage="Save" />
          ) : (
            <FormattedMessage id="action.load" defaultMessage="Load" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
